import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import type { LatencyTableRow } from "./LatencyTableRow";
import { BoundaryHeader, UnitCell } from "./observabilityCells";
import styles from "./ObservabilityPage.module.css";

/** 두 percentile 이 같은 설명을 쓰므로 툴팁 문구를 한 곳에 둔다. */
const PERCENTILE_HINT =
  "고정 histogram 으로 계산해 값이 든 bucket 의 상한이다. 그보다 정밀한 수치가 아니다. " +
  "빈 칸은 60초 초과(열린) 구간이라 상한을 못 만든 경우이고 0 이 아니다.";

/**
 * 지연 열이다. 창 집계와 **실측 최대**를 경계선으로 구분한다.
 *
 * 그 경계가 이 화면의 목적이다. 창 요약은 "느려졌다" 는 사실만 알려준다. 어느 호출이 느렸는지는
 * 실제로 가장 오래 걸린 호출 하나와 그 `request_id` 가 알려준다. 요청을 받은 쪽 행과 보낸 쪽 행에
 * 같은 `request_id` 가 있다. 두 행을 그 값으로 연결하면 "이 API 의 30초는 상류 hop 하나였다" 를
 * 알 수 있다. 이 부분에서 이 표가 Cloud Logging 을 대신한다.
 *
 * 모든 열에서 머리글 정렬을 쓸 수 있다. 기본 순서는 실측 최대 내림차순이다(`createLatencyRows`).
 * 서버 순서인 호출 수 내림차순을 그대로 쓰면 "드물지만 느린" 행이 수백 행 아래에 가려진다.
 */
export function createLatencyColumns(): readonly DataTableColumn<LatencyTableRow>[] {
  return [
    {
      key: "surface",
      header: "표면",
      width: "110px",
      sortable: "both",
      render: (row) => <span className={styles.podMetricValue}>{row.surface}</span>,
    },
    {
      key: "operation",
      header: "operation",
      width: "minmax(280px, 1fr)",
      sortable: "both",
      render: (row) => (
        <span className={styles.fingerprint} title={row.operation}>
          {row.operation}
        </span>
      ),
    },
    {
      key: "calls",
      header: "호출",
      width: "110px",
      align: "right",
      sortable: "both",
      render: (row) => <span className={styles.numeric}>{row.calls}</span>,
    },
    {
      key: "errors",
      header: "오류",
      width: "minmax(120px, 150px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.errors} />,
    },
    {
      key: "p50",
      header: <PercentileHeader label="p50" />,
      width: "110px",
      align: "right",
      sortable: "both",
      render: (row) => <span className={styles.numeric}>{row.p50}</span>,
    },
    {
      key: "p95",
      header: <PercentileHeader label="p95" />,
      width: "110px",
      align: "right",
      sortable: "both",
      render: (row) => <span className={styles.numeric}>{row.p95}</span>,
    },
    {
      // 실측 최대 묶음의 시작이다. 집계가 아니라 실제로 있었던 호출 하나다.
      key: "observedMax",
      header: <BoundaryHeader label="실측 최대" />,
      width: "minmax(170px, 200px)",
      sortable: "both",
      render: (row) => <UnitCell view={row.observedMax} boundary />,
    },
    {
      key: "requestId",
      header: "request_id",
      // uuid 36자가 모두 보여야 두 행을 눈으로 대조할 수 있다. 이 열은 이 대조에만 쓴다.
      width: "minmax(300px, 340px)",
      sortable: "both",
      render: (row) => (
        <span className={styles.fingerprint} title={row.requestId}>
          {row.requestId}
        </span>
      ),
    },
  ];
}

/** percentile 열 머리다. 값이 bucket 상한이라는 뜻을 열 머리가 설명하고, 행은 반복하지 않는다. */
function PercentileHeader({ label }: { label: string }): ReactNode {
  return (
    <HatTextTooltip text={PERCENTILE_HINT}>
      <span>{label}</span>
    </HatTextTooltip>
  );
}
